// src/App.js
import React from "react";
import { Routes, Route, Navigate, Outlet, Link } from "react-router-dom";

import { AuthProvider, useAuth } from "./context/AuthContext";
import NavBar from "./components/navbar";
import Login from "./pages/login";

/* Pagine */
import Nuovo from "./pages/Nuovo";
import Modifica from "./pages/Modifica";
import Elenco from "./pages/Elenco";
import Agenda from "./pages/agenda";
import AgendaPro from "./pages/AgendaPro";
import AgendaCall from "./pages/AgendaCall";
import AgendaPosa from "./pages/AgendaPosa";
import UtentiPage from "./pages/UtentiPage";
import ListinoAdmin from "./pages/ListinoAdmin";
import AccessMatrix from "./pages/AccessMatrix";
import Statistiche from "./pages/statistiche";
import Gestione from "./pages/gestione";

/* ============ Error Boundary ============ */
class ErrorBoundary extends React.Component {
  constructor(props){ super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error){ return { error }; }
  componentDidCatch(err, info){ console.error("[UI CRASH]", err, info); }
  render(){
    if (this.state.error) {
      return (
        <div style={{ padding: 16, margin: 16, border: "2px solid #c00", borderRadius: 10, background: "#fff" }}>
          <h3 style={{ marginTop: 0, color: "#c00" }}>Si è verificato un errore nell’interfaccia</h3>
          <pre style={{ whiteSpace: "pre-wrap" }}>{String(this.state.error?.message || this.state.error || "Error")}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ============ util pages ============ */
function NotFound() {
  return (
    <div style={{ padding: 24 }}>
      <h2>Pagina non trovata</h2>
      <p>Controlla l’URL oppure torna alla home.</p>
      <Link to="/elenco">Vai all’elenco</Link>
    </div>
  );
}
function NotAllowed() {
  return (
    <div style={{ padding: 24 }}>
      <h2>Permesso negato</h2>
      <p>Non hai i permessi per accedere a questa pagina.</p>
      <Link to="/elenco">Torna all’elenco</Link>
    </div>
  );
}

/* ============ guards semplici ============ */
function RequireAuth({ children }) {
  const { ready, isAuthenticated } = useAuth();
  if (!ready) return null; // qui puoi mostrare uno skeleton/spinner
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}
function RequireRole({ roles = [], children }) {
  const { ready, isAuthenticated, hasRole } = useAuth();
  if (!ready) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles.length && !(typeof hasRole === "function" ? hasRole(...roles) : false)) {
    return <NotAllowed />;
  }
  return children ?? <Outlet />;
}

/* ======= Guard: capability (una o più) OPPURE ruolo =======
   Supporta sia `cap` singolo che `caps` array.
   Esempio uso:
   <Route element={<RequireCapOrRole caps={['gestione.access','access.admin']} roles={['admin','formatore']} />}>
     <Route path="/gestione" element={<Gestione/>} />
   </Route>
*/
function RequireCapOrRole({ cap, caps = [], roles = [], children }) {
  const { ready, isAuthenticated, hasRole, hasCap, user } = useAuth();
  if (!ready) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const capList = Array.isArray(caps) && caps.length ? caps : (cap ? [cap] : []);

  const roleOk = roles.length
    ? (typeof hasRole === "function" ? hasRole(...roles) : false)
    : false;

  const hasCapFn = (code) =>
    (typeof hasCap === "function" ? hasCap(code) : false) ||
    (Array.isArray(user?.caps) && user.caps.includes(code));

  const capOk = capList.length ? capList.some(hasCapFn) : false;

  return (roleOk || capOk) ? (children ?? <Outlet />) : <NotAllowed />;
}

/* ============ root intelligente ============ */
function Root() {
  const { ready, isAuthenticated } = useAuth();
  if (!ready) return null;
  return isAuthenticated ? <Navigate to="/elenco" replace /> : <Login />;
}

/* ============ layout protetto ============ */
function ProtectedLayout() {
  return (
    <div className="app-shell">
      <NavBar />
      <main className="page-container">
        <Outlet />
      </main>
    </div>
  );
}

/* ============ APP ============ */
export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        {/* Il Router (BrowserRouter/HashRouter) è montato in index.js */}
        <Routes>
          {/* Pubblica */}
          <Route path="/" element={<Root />} />
          <Route path="/login" element={<Login />} />

          {/* Area protetta */}
          <Route
            element={
              <RequireAuth>
                <ProtectedLayout />
              </RequireAuth>
            }
          >
            {/* Accesso a tutti i ruoli loggati */}
            <Route path="/elenco" element={<Elenco />} />
            <Route path="/nuovo" element={<Nuovo />} />
            <Route path="/nuovo/:id" element={<Modifica />} />
            <Route path="/modifica/:id" element={<Modifica />} />
            <Route path="/agenda" element={<Agenda />} />
            <Route path="/agenda-pro" element={<AgendaPro />} />

            {/* === Gestione → capability (gestione.access OR access.admin) **oppure** ruolo (admin/formatore) === */}
            <Route
              element={
                <RequireCapOrRole
                  caps={["gestione.access", "access.admin"]} // compat: vecchio & nuovo codice permesso
                  roles={["admin", "formatore"]}
                />
              }
            >
              <Route path="/gestione" element={<Gestione />} />
            </Route>
            {/* Compat: vecchio URL con maiuscola → redirect */}
            <Route path="/Gestione" element={<Navigate to="/gestione" replace />} />

            {/* SOLO centralino, formatore, admin */}
            <Route element={<RequireRole roles={["centralino", "formatore", "admin"]} />}>
              <Route path="/agenda-call" element={<AgendaCall />} />
              <Route path="/utenti" element={<UtentiPage />} />
            </Route>

            {/* SOLO admin */}
            <Route element={<RequireRole roles={["admin"]} />}>
              <Route path="/acl" element={<AccessMatrix />} />
              <Route path="/listino" element={<ListinoAdmin />} />
            </Route>

            {/* Statistiche → admin **o** formatore */}
            <Route element={<RequireRole roles={["admin", "formatore"]} />}>
              <Route path="/statistiche" element={<Statistiche />} />
            </Route>

            {/* Agenda POSA → centralino, formatori, responsabili, admin, senior_posa */}
            <Route element={<RequireRole roles={["centralino","formatore","responsabile","admin","senior_posa"]} />}>
              <Route path="/agenda-posa" element={<AgendaPosa />} />
            </Route>

            {/* fallback interno all'area protetta */}
            <Route path="*" element={<Navigate to="/elenco" replace />} />
          </Route>

          {/* fallback globale */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </ErrorBoundary>
  );
}
